// lib/deploy/idleReload.ts
//
// Política de AUTO-recarga de BuildWatcher cuando hay un deployment nuevo.
//
// Contexto (egress 2026-08-15): tras mergear #1528 (que cortó un lazo de
// sondeo del dashboard/POS), las tablets siguieron corriendo el bundle VIEJO
// —con el lazo— durante toda la cena, porque BuildWatcher solo muestra
// "Actualizar ahora" y nadie lo tocó. Medido: /[slug]/dashboard 2.534/h y
// /[slug]/pos 1.971/h de renders (1 req/s por local) con el fix ya en prod.
// Un fix de cliente que nadie recarga no existe.
//
// La política original ("NUNCA auto-reload: no borrar un carrito en curso")
// sigue siendo el norte, pero su premisa era más estrecha de lo que parece:
// el carrito del POS (useCart, zustand+localStorage) y el estado "esperando
// terminal" (C-05) sobreviven un reload. Lo que de verdad no se interrumpe es
// un COBRO en vuelo o una acción de cocina en curso (print/cola offline).
//
// Regla: se auto-recarga SOLO si
//   1. la ruta es superficie de operador (watchesDeployments), y
//   2. hay algo pendiente: deployment nuevo, deploy NECESARIO (`required`, ver
//      lib/deploy/clientEpoch.ts) o un error de desfase de versión (`skew`), y
//   3. (S5-1) si el local está EN SERVICIO (lib/deploy/serviceWindow.ts), lo
//      pendiente es urgente (required o skew). Un deploy normal en servicio
//      devuelve "defer": indicador discreto y nada más. Con 40-50 deploys al
//      día, recargar en cada pausa de 90 s eran ~40 arranques en frío por
//      tablet en plena cena (auditoría 2026-10-07, P1-15), y
//   4. NO hay razones de ocupación registradas (holdReload/releaseReload —
//      cada superficie declara las suyas; este módulo no conoce POS ni KDS), y
//   5. hay red: recargar sin conexión pinta /offline.html en vez del POS, y
//   6. el operador lleva ≥ IDLE_MS sin tocar la pantalla (pointer/key/touch),
//      para no arrancarle la vista a alguien que está trabajando aunque no
//      haya carrito, y
//   7. pasó un jitter aleatorio (0..JITTER_MS) desde que se detectó, para no
//      tumbar todas las tablets de un local en el mismo segundo, y
//   8. no hubo otra recarga automática hace < MIN_AUTO_RELOAD_GAP_MS (si tras
//      recargar el bundle siguiera viejo, sin esto entraría en bucle).
// Si no se cumple, se queda el aviso y se re-evalúa cada RECHECK_MS.
//
// Funciones puras + un registro mínimo, para probar sin DOM.

export const IDLE_MS = 90_000
export const JITTER_MS = 60_000
/** Reevaluación mientras haya deployment nuevo pero no se cumplan las condiciones. */
export const RECHECK_MS = 15_000
/** Separación mínima entre dos recargas AUTOMÁTICAS de la misma tablet (anti-bucle). */
export const MIN_AUTO_RELOAD_GAP_MS = 10 * 60_000
/** localStorage: epoch ms de la última recarga automática. */
export const LAST_AUTO_RELOAD_KEY = "redy:last-auto-reload"

// ── Registro de razones de ocupación ────────────────────────────────────────
// Un Set de strings: "pos-charge", "kds-print", "kds-offline-queue"… Idempotente:
// hold dos veces la misma razón cuenta una; release de algo no registrado es no-op.
const reasons = new Set<string>()

export function holdReload(reason: string): void {
  reasons.add(reason)
}
export function releaseReload(reason: string): void {
  reasons.delete(reason)
}
export function busyReasons(): string[] {
  return [...reasons]
}
export function isBusy(): boolean {
  return reasons.size > 0
}
/** Solo tests. */
export function __resetBusyForTests(): void {
  reasons.clear()
}

// ── Decisión ────────────────────────────────────────────────────────────────
export interface IdleReloadInput {
  /** watchesDeployments(pathname) */
  operatorSurface: boolean
  /** hay deployment nuevo detectado */
  outdated: boolean
  /** el deploy vivo declaró que este bundle ya no sirve (CLIENT_EPOCH) */
  required: boolean
  /** una server action o un chunk falló por desfase de versión */
  skew: boolean
  /** el local está en servicio (isInService) */
  inService: boolean
  /** navigator.onLine !== false */
  online: boolean
  /** isBusy() */
  busy: boolean
  /** ms desde la última interacción del operador */
  msSinceInteraction: number
  /** ms desde que se detectó lo pendiente (deploy nuevo o skew) */
  msSinceOutdated: number
  /** jitter asignado a ESTA instancia al detectar (0..JITTER_MS) */
  jitterMs: number
  /** ms desde la última recarga automática de esta tablet; null si no hay marca */
  msSinceLastAutoReload: number | null
}

/**
 * - "reload": recargar ya.
 * - "wait":   se recargará sola cuando se cumplan las condiciones (aviso visible).
 * - "defer":  versión nueva NO urgente durante el servicio: no recargar; solo
 *             el indicador discreto. Se re-evalúa (al cerrar pasa a "wait").
 * - "no":     nada que hacer en esta superficie.
 */
export type IdleReloadDecision = "reload" | "wait" | "defer" | "no"

export function decideIdleReload(i: IdleReloadInput): IdleReloadDecision {
  if (!i.operatorSurface) return "no"
  const urgent = i.required || i.skew
  if (!i.outdated && !urgent) return "no"
  if (i.inService && !urgent) return "defer"
  if (i.busy) return "wait"
  if (!i.online) return "wait"
  if (i.msSinceInteraction < IDLE_MS) return "wait"
  if (i.msSinceOutdated < i.jitterMs) return "wait"
  if (i.msSinceLastAutoReload !== null && i.msSinceLastAutoReload < MIN_AUTO_RELOAD_GAP_MS) return "wait"
  return "reload"
}

/**
 * ms desde la última recarga automática según la marca en storage, o null si
 * no hay marca, es basura, está en el futuro o el storage lanza (modo privado).
 */
export function readLastAutoReload(
  storage: Pick<Storage, "getItem"> | null | undefined,
  nowMs: number,
): number | null {
  try {
    const raw = storage?.getItem(LAST_AUTO_RELOAD_KEY)
    if (!raw || !/^\d+$/.test(raw)) return null
    const elapsed = nowMs - Number(raw)
    return elapsed >= 0 ? elapsed : null
  } catch {
    return null
  }
}

/** Jitter determinista a partir de un valor 0..1 (Math.random() en runtime; fijo en tests). */
export function jitterFrom(unit: number): number {
  const u = Number.isFinite(unit) ? Math.min(Math.max(unit, 0), 1) : 0
  return Math.floor(u * JITTER_MS)
}
